import { useState } from "react";
import { memories } from "@/mocks/events";

// 分成左中右三欄瀑布流
const col1 = [0, 3, 6, 9];   // id index: 1,4,7,10
const col2 = [1, 4, 7];       // id index: 2,5,8
const col3 = [2, 5, 8];       // id index: 3,6,9

export default function MemoriesSection() {
  const [hoveredId, setHoveredId] = useState<number | null>(null);
  const [showAll, setShowAll] = useState(false);

  const visibleCol1 = showAll ? col1 : col1.slice(0, 2);
  const visibleCol2 = showAll ? col2 : col2.slice(0, 2);
  const visibleCol3 = showAll ? col3 : col3.slice(0, 2);

  const PhotoCard = ({ idx }: { idx: number }) => {
    const item = memories[idx];
    if (!item) return null;
    return (
      <div
        className="relative rounded-2xl overflow-hidden cursor-pointer group mb-4"
        onMouseEnter={() => setHoveredId(item.id)}
        onMouseLeave={() => setHoveredId(null)}
      >
        <img
          src={item.image}
          alt={item.title}
          className="w-full object-cover object-top transition-transform duration-500 group-hover:scale-105"
          style={{ display: "block" }}
        />
        {/* Overlay */}
        <div
          className={`absolute inset-0 bg-gradient-to-t from-black/70 via-black/20 to-transparent flex flex-col justify-end p-4 transition-opacity duration-300 ${
            hoveredId === item.id ? "opacity-100" : "opacity-0"
          }`}
        >
          <p className="text-white font-bold text-sm leading-tight">{item.title}</p>
          <div className="flex items-center gap-1.5 mt-1">
            <span className="w-3 h-3 flex items-center justify-center">
              <i className="ri-calendar-line text-white/70 text-xs" />
            </span>
            <p className="text-white/70 text-xs">{item.date}</p>
          </div>
        </div>
      </div>
    );
  };

  return (
    <section id="memories" className="py-20 md:py-28 bg-white">
      <div className="max-w-7xl mx-auto px-4 md:px-8">
        {/* Header */}
        <div className="flex flex-col md:flex-row md:items-end justify-between mb-12 gap-4">
          <div>
            <p className="text-red-600 text-sm font-semibold tracking-widest uppercase mb-3">
              Memories
            </p>
            <h2
              className="text-3xl md:text-5xl font-black text-gray-900"
              style={{ fontFamily: "'Noto Serif TC', serif" }}
            >
              那些被記住的瞬間
            </h2>
          </div>
          <div className="flex items-center gap-6">
            <p className="text-gray-400 text-sm">
              已舉辦 <span className="text-red-600 font-bold text-base">500+</span> 場活動
            </p>
            <a
              href="https://www.instagram.com/rappy.meme/"
              target="_blank"
              rel="nofollow noreferrer"
              className="inline-flex items-center gap-2 text-sm text-gray-600 font-medium hover:text-red-600 transition-colors cursor-pointer whitespace-nowrap"
            >
              <span className="w-5 h-5 flex items-center justify-center">
                <i className="ri-instagram-line text-base" />
              </span>
              更多精彩 @rappy.meme
            </a>
          </div>
        </div>

        {/* Masonry Grid - 3 columns */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {/* Col 1 */}
          <div>
            {visibleCol1.map((idx) => (
              <PhotoCard key={idx} idx={idx} />
            ))}
          </div>
          {/* Col 2 */}
          <div className="sm:mt-8">
            {visibleCol2.map((idx) => (
              <PhotoCard key={idx} idx={idx} />
            ))}
          </div>
          {/* Col 3 */}
          <div className="sm:mt-4 hidden lg:block">
            {visibleCol3.map((idx) => (
              <PhotoCard key={idx} idx={idx} />
            ))}
          </div>
        </div>

        {/* Show More / Less */}
        <div className="text-center mt-8">
          <button
            onClick={() => setShowAll(!showAll)}
            className="inline-flex items-center gap-2 px-8 py-3 rounded-full border-2 border-gray-200 text-gray-600 font-semibold text-sm hover:border-red-500 hover:text-red-600 transition-all duration-200 cursor-pointer whitespace-nowrap"
          >
            <span className="w-4 h-4 flex items-center justify-center">
              <i className={`${showAll ? "ri-arrow-up-line" : "ri-add-line"} text-base`} />
            </span>
            {showAll ? "收起照片" : "查看更多回憶"}
          </button>
        </div>

        {/* Quote */}
        <div className="mt-16 text-center">
          <p
            className="text-2xl md:text-3xl font-bold text-gray-800 max-w-2xl mx-auto leading-relaxed"
            style={{ fontFamily: "'Noto Serif TC', serif" }}
          >
            「在沒有網路訊號的森林裡，
            <br />
            人與人之間的信號最強。」
          </p>
          <p className="text-gray-400 text-sm mt-4">— 娛樂紅歡密 創辦人</p>
        </div>

        {/* IG Follow Banner */}
        <div className="mt-16 rounded-3xl overflow-hidden relative">
          <img
            src="https://public.readdy.ai/ai/img_res/96f9cc55ee4e09a28f99ca6652aebd6e.jpg"
            alt="更多精彩活動照片"
            className="w-full h-64 md:h-72 object-cover object-center"
          />
          <div className="absolute inset-0 bg-gradient-to-r from-black/75 via-black/50 to-black/30" />
          <div className="absolute inset-0 flex flex-col md:flex-row items-center justify-between px-8 md:px-14 py-8 gap-6">
            {/* Left text */}
            <div>
              <p className="text-white/70 text-sm font-medium mb-2 tracking-widest uppercase">Follow Us</p>
              <h3
                className="text-white font-black text-2xl md:text-3xl mb-2 leading-tight"
                style={{ fontFamily: "'Noto Serif TC', serif" }}
              >
                還有更多精彩瞬間
                <br />
                在我們的 IG 等你！
              </h3>
              <p className="text-white/60 text-sm">
                每場活動的真實照片、幕後花絮，都在這裡
              </p>
            </div>

            {/* Right: IG card */}
            <div className="shrink-0 flex flex-col items-center gap-4">
              {/* IG handle */}
              <div className="flex items-center gap-3 bg-white/15 backdrop-blur-sm border border-white/25 rounded-2xl px-5 py-3">
                <div className="w-10 h-10 flex items-center justify-center rounded-xl bg-gradient-to-br from-orange-400 via-red-500 to-red-700">
                  <i className="ri-instagram-line text-white text-lg" />
                </div>
                <div>
                  <p className="text-white font-bold text-sm">@rappy.meme</p>
                  <p className="text-white/60 text-xs">Instagram</p>
                </div>
              </div>
              <a
                href="https://www.instagram.com/rappy.meme/"
                target="_blank"
                rel="nofollow noreferrer"
                className="inline-flex items-center gap-2 px-7 py-3 rounded-full bg-white text-gray-900 font-bold text-sm hover:bg-red-50 transition-all duration-200 cursor-pointer whitespace-nowrap"
              >
                <i className="ri-instagram-line text-base" />
                立即追蹤
              </a>
              <a
                href="https://www.facebook.com/Red.shrimp.close/"
                target="_blank"
                rel="nofollow noreferrer"
                className="inline-flex items-center gap-2 text-white/70 text-xs font-medium hover:text-white transition-colors cursor-pointer whitespace-nowrap"
              >
                <i className="ri-facebook-fill text-sm" />
                也可以追蹤 Facebook
              </a>
            </div>
          </div>
        </div>

      </div>
    </section>
  );
}
